import { useState, useEffect } from "react";
import { useCards } from "@/features/card/hooks/useCards";
import { storageService } from "@/shared/services/localStorage.service";

type UseOnboardingModalReturn = {
  isModalOpen: boolean
  openModal: (cardId?: string) => void
  closeModal: () => void
  hasUnactivatedCard: boolean
  unactivatedCardId: string | null
};

const ONBOARDING_MODAL_CLOSED_KEY = "onboarding_modal_closed";

export const useOnboardingModal = (): UseOnboardingModalReturn => {
  const { unactivatedCard } = useCards();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [targetCardId, setTargetCardId] = useState<string | null>(null);

  const hasUnactivatedCard = !!unactivatedCard;

  // Check if the modal has been manually closed by the user
  const isModalClosedByUser = (): boolean => {
    try {
      const closedTimestamp = storageService.getItem(ONBOARDING_MODAL_CLOSED_KEY);
      if (!closedTimestamp) return false;

      const closedTime = parseInt(closedTimestamp, 10);
      const currentTime = Date.now();
      const sessionDuration = 24 * 60 * 60 * 1000; // 24 heures en millisecondes

      // Modal stay closed for 24 hours after manual close
      return (currentTime - closedTime) < sessionDuration;
    }
    catch {
      return false;
    }
  };

  const openModal = (cardId?: string): void => {
    setIsModalOpen(true);
    setTargetCardId(cardId ?? null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTargetCardId(null);
    storageService.setItem(ONBOARDING_MODAL_CLOSED_KEY, Date.now().toString());
  };

  useEffect(() => {
    if (!hasUnactivatedCard || isModalClosedByUser()) return;
    const timer = setTimeout(() => openModal(unactivatedCard.id), 1000);
    return () => clearTimeout(timer);
  }, [unactivatedCard?.id, hasUnactivatedCard]);

  return {
    isModalOpen,
    openModal,
    closeModal,
    hasUnactivatedCard,
    unactivatedCardId: targetCardId,
  };
};
